import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Alert,
  Tooltip,
  Divider,
  Stack,
  Chip,
  useMediaQuery,
} from '@mui/material'
import { Delete, CloudUpload, Visibility, InfoOutlined } from '@mui/icons-material'
import { supabase } from './supabaseClient'
import { useTranslation } from 'react-i18next' // Import i18next

const bucketName = 'documents'

export default function ManageDocuments() {
  const { t } = useTranslation() // Hook for translations
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [documents, setDocuments] = useState([])
  const [user, setUser] = useState(null)
  const [employmentStatus, setEmploymentStatus] = useState(null)
  const [docRequirements, setDocRequirements] = useState([])
  const matches = useMediaQuery((theme) => theme.breakpoints.up('sm'))

  useEffect(() => {
    fetchUser()
  }, [])

  useEffect(() => {
    if (user) {
      fetchProfile()
    }
  }, [user])

  useEffect(() => {
    if (employmentStatus) {
      fetchDocRequirements()
    }
  }, [employmentStatus])

  useEffect(() => {
    if (docRequirements.length > 0) {
      fetchDocuments()
    }
  }, [docRequirements])

  async function fetchUser() {
    const { data, error } = await supabase.auth.getUser()
    if (error) {
      console.error(t('documents.errorFetchUser'), error)
    } else if (data?.user) {
      setUser(data.user)
    }
  }

  async function fetchProfile() {
    if (!user) return
    const { data, error } = await supabase
      .from('profiles')
      .select('employment_status')
      .eq('id', user.id)
      .single()
    if (error) {
      console.error(t('documents.errorFetchProfile'), error)
    } else {
      setEmploymentStatus(data.employment_status || 'student')
    }
  }

  async function fetchDocRequirements() {
    const { data, error } = await supabase
      .from('document_requirements')
      .select('doc_type')
      .eq('employment_status', employmentStatus)
    if (error) {
      console.error(t('documents.errorFetchRequirements'), error)
    } else {
      const reqs = data.map((item) => item.doc_type)
      setDocRequirements(reqs)
    }
  }

  async function fetchDocuments() {
    if (!user) return
    setLoading(true)
    let allDocuments = []
    for (const docType of docRequirements) {
      const { data, error } = await supabase.storage.from(bucketName).list(`${user.id}/${docType}`)
      if (error) {
        console.error(`${t('documents.errorFetch')} ${docType}:`, error)
      } else if (data) {
        allDocuments = allDocuments.concat(
          data.map((doc) => ({ ...doc, path: `${user.id}/${docType}/${doc.name}`, docType }))
        )
      }
    }
    setDocuments(allDocuments)
    setLoading(false)
  }

  async function uploadDocument(event, docType) {
    try {
      if (!user) throw new Error(t('documents.errorNoUser'))
      setUploading(true)
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error(t('documents.errorNoFile'))
      }
      const file = event.target.files[0]
      const filePath = `${user.id}/${docType}/${file.name}`
      const { error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, { upsert: true })
      if (uploadError) throw uploadError
      fetchDocuments()
    } catch (error) {
      alert(error.message)
    } finally {
      setUploading(false)
    }
  }

  async function deleteDocument(event, filePath) {
    try {
      event.stopPropagation()
      setDeleting(true)
      const { error } = await supabase.storage.from(bucketName).remove([filePath])
      if (error) throw error
      fetchDocuments()
    } catch (error) {
      alert(error.message)
    } finally {
      setDeleting(false)
    }
  }

  async function getFileUrl(filePath) {
    const { data, error } = await supabase.storage.from(bucketName).getPublicUrl(filePath)
    if (error) {
      console.error(t('documents.errorFileUrl'), error)
      return ''
    }
    return data.publicUrl
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: { xs: 2, sm: 3 } }}>
      {/* Introduction Section */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 4 }, mb: 3, bgcolor: 'primary.50', borderRadius: 2 }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          mb={2}
        >
          <Typography
            variant="h4"
            color="primary.main"
            fontWeight="500"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            {t('documents.manage')}
          </Typography>
          <Tooltip title={t('documents.securityInfo')}>
            <InfoOutlined color="primary" />
          </Tooltip>
        </Stack>
        <Typography variant="body1" mb={2}>
          {t('documents.intro')}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('documents.requirements')}
        </Alert>
      </Paper>

      {/* Document Upload Section */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3, borderRadius: 2 }}>
        <Typography
          variant="h5"
          mb={3}
          color="text.primary"
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
          }}
        >
          {t('documents.required')}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {docRequirements.map((docType) => {
              const uploadedDoc = documents.find((doc) => doc.path.includes(`/${docType}/`))
              const title = docType
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')

              return (
                <Box
                  key={docType}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 1,
                    bgcolor: uploadedDoc ? 'success.50' : 'grey.50',
                    border: 1,
                    borderColor: uploadedDoc ? 'success.200' : 'grey.200',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={2}
                  >
                    <Box flex={1}>
                      <Typography
                        variant="h6"
                        color="text.primary"
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: { xs: 0.5, sm: 0 } }}
                      >
                        {t(`documents.${docType}Description`)}
                      </Typography>
                    </Box>

                    {!uploadedDoc ? (
                      <Box sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}>
                        <input
                          type="file"
                          accept=".pdf, .jpg, .jpeg, .png"
                          onChange={(event) => uploadDocument(event, docType)}
                          disabled={uploading}
                          hidden
                          id={`upload-${docType}`}
                        />
                        <label htmlFor={`upload-${docType}`}>
                          <Button
                            variant="contained"
                            component="span"
                            disabled={uploading}
                            startIcon={<CloudUpload />}
                            sx={{
                              minWidth: 140,
                              width: { xs: '100%', sm: 'auto' },
                            }}
                          >
                            {uploading ? <CircularProgress size={20} /> : t('documents.upload')}
                          </Button>
                        </label>
                      </Box>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                          mt: { xs: 1, sm: 0 },
                        }}
                      >
                        <Chip
                          label={t('documents.uploaded')}
                          color="success"
                          size="small"
                          sx={{ display: { xs: 'none', sm: 'flex' } }}
                        />
                        <Tooltip title={t('documents.preview')}>
                          <IconButton
                            onClick={async () =>
                              window.open(await getFileUrl(uploadedDoc.path), '_blank')
                            }
                            color="primary"
                            size={matches ? 'medium' : 'small'}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('documents.delete')}>
                          <IconButton
                            onClick={(event) => deleteDocument(event, uploadedDoc.path)}
                            disabled={deleting}
                            color="error"
                            size={matches ? 'medium' : 'small'}
                          >
                            {deleting ? <CircularProgress size={20} /> : <Delete />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        )}
      </Paper>

      {/* Document Guidelines */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, bgcolor: 'grey.50' }}>
        <Typography
          variant="h6"
          mb={2}
          color="text.primary"
          sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
        >
          {t('documents.guidelines')}
        </Typography>
        <Stack spacing={2}>
          {['format', 'size', 'validity', 'quality'].map((item) => (
            <Typography
              key={item}
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              • {t(`documents.guideline_${item}`)}
            </Typography>
          ))}
        </Stack>
      </Paper>
    </Box>
  )
}
